import {useMutation, useQueryClient} from "@tanstack/react-query";
import {deleteImage, uploadImageForGallery, uploadImageForProfile,} from "api/image.api.ts";
import {updateProfileInformation} from "api/teacher.api.ts";
import {useAuth} from "context/AuthContext.jsx";
import {useUpdateTeacher} from "context/UpdateTeacherContext";
import {useToast} from "hooks/index.js";
import {useSelectImages} from "hooks/useSelectImages.js";
import {useState} from "react";
import {useMediaQuery} from "react-responsive";
import styled from "styled-components";
import {ChevronRight} from "ui/icons/ChevronRight.jsx";
import {convertHexToRGBA, defaultThemeColors, fonts, pageContainerStyles, themeColor,} from "ui/theme/index.js";
import {Listing} from "./section/listing/Listing.jsx";
import {PhotosPage} from "./section/PhotosPage.jsx";
import {Policies} from "./section/policies/Policies.jsx";
import {SchedulePage} from "./section/schedulePage/SchedulePage.jsx";
import {TypeAndPrice} from "./section/TypeAndPrice.jsx";
import {isGroupTypeClass, isPrivateTypeClass} from "conts/profileConst.js";
import {
    validateListingSection,
    validatePhotosSection,
    validatePoliciesSection,
    validatePriceSection,
} from "./sectionValidation"

export const ClassListing = () => {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState("listing");
    const [sectionErrors, setSectionErrors] = useState({
        listing: {},
        price: {},
        photos: {},
        schedule: {},
        policy: {},
    })

    const {userData} = useAuth();
    const {
        formData,
        changeFormData: changeTeacherInfo,
        setFormData,
    } = useUpdateTeacher();

    const [loading, setLoading] = useState(false);
    const [isError, setIsError] = useState(null);
    const isMobile = useMediaQuery({query: "(max-width: 768px)"});
    const [isMobileOverlayVisible, setMobileOverlayVisibility] = useState(false);

    const {selectedImages, setSelectedImages,  handleImageDeselect} =
        useSelectImages(userData?.gallery || []);

    const {mutate: submitProfile} = useMutation({
        mutationFn: updateProfileInformation,
        onError: (err) => {
            setLoading(false);
            useToast("error", "Update Profile error");
        },
        onSuccess: (data) => {
            useToast("success", "Changes saved");
            setLoading(false);
            queryClient.invalidateQueries(["userDetail"]);
        },
    });

    const {mutateAsync: startUploadingImages} = useMutation({
        mutationFn: uploadImageForGallery,
    });

    const addImagesToGallery = async (uploadedProfileImage) => {
        try {
            let newGallery = [];
            for (const photoInfo of selectedImages) {
                if (photoInfo?.deleted) {
                    continue;
                }
                if (photoInfo?.uploaded) {
                    newGallery.push(photoInfo);
                    continue;
                }

                const res = await startUploadingImages(photoInfo);

                newGallery.push({
                    ...photoInfo,
                    name: "",
                    type: "",
                    uri: res,
                    uploaded: true,
                    deleted: false,
                });
            }

            setFormData((prevState) => ({
                ...prevState,
                gallery: newGallery,
            }));

            const mappedGallery = newGallery?.map((it) => {
                const idx = it.uri.lastIndexOf("/");
                return {
                    name: it.uri.slice(idx + 1),
                };
            });

            let prices = {
                price_per_group_class: 0,
                price_per_private_class: 0,
                price_per_online_class: 0,
                price_per_online_class_estimate_earning: 0,
                price_per_private_class_estimate_earning: 0,
                price_per_group_class_estimate_earning: 0,
            }

            //Online
            if (formData?.online_type !== "offline") {
                prices.price_per_online_class = formData?.price_per_online_class || 0;
                prices.price_per_online_class_estimate_earning = formData?.price_per_online_class_estimate_earning || 0;

            }

            // Private
            if (isPrivateTypeClass(formData?.type_of_class) && formData?.online_type !== "online") {
                prices.price_per_private_class = formData?.price_per_private_class || 0;
                prices.price_per_private_class_estimate_earning = formData?.price_per_private_class_estimate_earning || 0;
            }

            // Group
            if (isGroupTypeClass(formData?.type_of_class)) {
                prices.price_per_group_class = formData?.price_per_group_class || 0;
                prices.price_per_group_class_estimate_earning = formData?.price_per_group_class_estimate_earning || 0;

            }

            submitProfile({
                ...formData,
                ...prices,
                number_of_students: formData?.number_of_students || 0,
                number_of_students_private_class:
                    formData?.number_of_students_private_class || 0,
                gallery: mappedGallery,
                profile_completion: "100",
                new_signup: false,
                profile_pic: uploadedProfileImage,
                online_type: formData?.online_type || "offline",
                locations_of_class: formData?.online_type === "online" ? "" : formData?.locations_of_class,
                neighborhood: formData?.online_type === "online" ? "" : formData?.neighborhood
            });
        } catch (error) {
            setLoading(false);
            console.error(error);
            useToast("error", "Upload image error");
        }
    };

    const {mutate: deleteUploadedImages} = useMutation({mutationFn:deleteImage});

    const deleteUploadedImagesFromGallery = () => {
        for (const photoInfo of selectedImages) {
            if (photoInfo?.deleted && photoInfo?.uploaded) {
                deleteUploadedImages(photoInfo, "gallery");
            }
        }
    };

    const {mutate: onUploadImage} = useMutation({
        mutationFn: uploadImageForProfile,
        onError: (err) => {
            setLoading(false);
            console.error("onUploadImage", err);
        },
        onSuccess: (data) => {
            const idx1 = data?.lastIndexOf("/");
            const uploadUri = data.slice(idx1 + 1);
            changeTeacherInfo("profile_pic", {
                ...formData.profile_pic,
                uri: data,
            });
            addImagesToGallery(uploadUri);
            deleteUploadedImagesFromGallery();
        },
    });


    const hasScheduleSlots = () => {
        const classAvailability = userData?.teacher?.slots?.filter(
            (classAv) => classAv.activeStatus
        );
        const onlineClassAvailability = userData?.teacher?.online_slots?.filter(
            (classAv) => classAv.activeStatus
        );

        const flattenedClasses = classAvailability?.flatMap((cls) => {
            return cls.teacher_class_timings;
        });
        const flattenedOnlineClasses = onlineClassAvailability?.flatMap((cls) => {
            return cls.teacher_class_timings;
        });

        if ((flattenedClasses?.length || 0) < 1 && (flattenedOnlineClasses?.length || 0) < 1) {
            useToast("error", "Add at least 1 class");
            setIsError("SCHEDULE")
            return false;
        }

        return true;
    }

    const onSubmit = () => {
        setIsError(null)

        if (!hasScheduleSlots()) return;

        setLoading(true);
        onUploadImage({image: formData.profile_pic, userType: "instructor"});
    };
    const validateAndSwitchTab = (newTab) => {
        if (newTab === activeTab) {
            if (isMobile) {
                setMobileOverlayVisibility(true)
            }
            setActiveTab(newTab)
            return
        }

        let errors = {}
        let isValid = true
        let errorMessage = ""

        switch (activeTab) {
            case "listing":
                errors = validateListingSection(formData, {
                    whatExpectMyClassOnline: formData?.expected_students_online || false,
                    whatParticipantsTakeClass: formData?.student_need_to_have_online || false,
                })
                if (Object.keys(errors).length > 0) {
                    isValid = false
                    setSectionErrors((prev) => ({ ...prev, listing: errors }))
                    errorMessage = "Please fill all required fields in Class Listing"
                    useToast("error", errorMessage)
                }
                break

            case "price":
                const selectedOption = require("conts/profileConst.js").allClassTypeOptions.find(
                    (option) => option.type_of_class === formData?.type_of_class && option.online_type === formData.online_type,
                )
                errors = validatePriceSection(formData, selectedOption)
                if (Object.keys(errors).length > 0) {
                    isValid = false
                    setSectionErrors((prev) => ({ ...prev, price: errors }))
                    errorMessage = "Please fill all required fields in Type & Price"
                    useToast("error", errorMessage)
                }
                break

            case "photos":
                errors = validatePhotosSection(selectedImages)
                if (Object.keys(errors).length > 0) {
                    isValid = false
                    setSectionErrors((prev) => ({ ...prev, photos: errors }))
                    errorMessage = "Please add at least 2 images"
                    useToast("error", errorMessage)
                }
                break

            case "policy":
                errors = validatePoliciesSection(formData)
                if (Object.keys(errors).length > 0) {
                    isValid = false
                    setSectionErrors((prev) => ({ ...prev, policy: errors }))
                    errorMessage = "Please fill all required fields in Cancellation & Discount Policy"
                    useToast("error", errorMessage)
                }
                break

            case "schedule":
                // Schedule validation will be handled by SchedulePage component
                break

            default:
                break
        }

        if (isValid) {
            setSectionErrors((prev) => ({
                ...prev,
                [activeTab]: {},
            }))
            if (isMobile) {
                setMobileOverlayVisibility(true)
            }
            setActiveTab(newTab)
        }
    }

    const handleMenuItemClick = (tab) => {
        validateAndSwitchTab(tab)
    }

    const getTitleMobile = () => {
        switch (activeTab) {
            case "listing":
                return "Class Listing";
            case "price":
                return "Type & Price";
            case "policy":
                return "Cancellation & Discount policies";
            case "photos":
                return "Photos";
            case "schedule":
                return "Schedule";
            default:
                break;
        }
    };

    const handleErrors = (value) => {
        setIsError(value)
    }

    return (
        <>
            <Container>
                <div className="title">
                    <Title>Class Listing</Title>
                </div>

                <div className="content">
                    <Sidebar>
                        <SidebarItem
                            active={activeTab === "listing"}
                            onClick={() => handleMenuItemClick("listing")}
                        >
                            Class Listing
                            <MobileWrapper hide>
                                <ChevronRight />
                            </MobileWrapper>
                        </SidebarItem>
                        <SidebarItem
                            active={activeTab === "price"}
                            onClick={() => handleMenuItemClick("price")}
                        >
                            Type & Price
                            <MobileWrapper hide>
                                <ChevronRight />
                            </MobileWrapper>
                        </SidebarItem>
                        <SidebarItem
                            active={activeTab === "policy"}
                            onClick={() => handleMenuItemClick("policy")}
                        >
                            Cancellation & Discount policies
                            <MobileWrapper hide>
                                <ChevronRight />
                            </MobileWrapper>
                        </SidebarItem>

                        <SidebarItem
                            active={activeTab === "photos"}
                            onClick={() => handleMenuItemClick("photos")}
                        >
                            Photos
                            <MobileWrapper hide>
                                <ChevronRight />
                            </MobileWrapper>
                        </SidebarItem>
                        <SidebarItem
                            active={activeTab === "schedule"}
                            onClick={() => handleMenuItemClick("schedule")}
                        >
                            Schedule
                            <MobileWrapper hide>
                                <ChevronRight />
                            </MobileWrapper>
                        </SidebarItem>
                    </Sidebar>

                    {isMobile && (
                        <MobileOverlay isVisible={isMobileOverlayVisible}>
                            <OverlayContent>
                                <OverlayHeader>
                                    <CloseButton
                                        onClick={() => setMobileOverlayVisibility(false)}
                                    >
                                        <ChevronRight
                                            style={{
                                                transform: "rotate(180deg)",
                                            }}
                                        />
                                    </CloseButton>
                                    <h2>{getTitleMobile()}</h2>
                                </OverlayHeader>
                                <OverlayBody>
                                    <SharedContent
                                        onSubmit={onSubmit}
                                        activeTab={activeTab}
                                        loading={loading}
                                        selectedImages={selectedImages}
                                        handleImageDeselect={handleImageDeselect}
                                        setSelectedImages={setSelectedImages}
                                    />
                                </OverlayBody>
                            </OverlayContent>
                        </MobileOverlay>
                    )}

                    {!isMobile && (
                        <SharedContent
                            onSubmit={onSubmit}
                            activeTab={activeTab}
                            loading={loading}
                            selectedImages={selectedImages}
                            handleImageDeselect={handleImageDeselect}
                            setSelectedImages={setSelectedImages}
                            handleErrors={handleErrors}
                        />
                    )}
                </div>
            </Container>
        </>
    );
};

const SharedContent = ({
                           onSubmit,
                           activeTab,
                           loading,
                           selectedImages,
                           handleImageDeselect,
                           setSelectedImages,
                           handleErrors,
                       }) => {
    return (
        <Content>
            <SidebarContent active={activeTab === "listing"}>
                <Listing onSubmit={onSubmit} loading={loading} handleErrors={handleErrors}/>
            </SidebarContent>
            <SidebarContent active={activeTab === "price"}>
                <TypeAndPrice onSubmit={onSubmit} loading={loading}/>
            </SidebarContent>
            <SidebarContent active={activeTab === "policy"}>
                <Policies onSubmit={onSubmit} loading={loading}/>
            </SidebarContent>

            <SidebarContent active={activeTab === "photos"}>
                <PhotosPage
                    onSubmit={onSubmit}
                    loading={loading}
                    selectedImages={selectedImages}
                    setSelectedImages={setSelectedImages}
                    handleImageDeselect={handleImageDeselect}
                />
            </SidebarContent>

            <SidebarContent active={activeTab === "schedule"}>
                <SchedulePage handleErrors={handleErrors}/>
            </SidebarContent>
        </Content>
    );
};

const Container = styled.div`
    ${pageContainerStyles}
    padding-bottom: 60px;
    display: flex;
    gap: 1rem;
    flex-direction: column;
    font-family: var(--font-secondary);
    overflow: visible;
    padding-top: 60px;
    min-height: var(--page-height);

    & .content {
        display: flex;

        @media (min-width: 1024px) {
            gap: 8rem;
        }
    }

    & .title {
        height: fit-content;
    }
`;

const Sidebar = styled.div`
    display: flex;
    flex-direction: column;
    top: 40px;
    height: fit-content;
    gap: 1rem;
    width: 100%;

    @media (min-width: 1024px) {
        min-width: 250px;
        width: auto;
        position: sticky;
    }
`;

const Title = styled.h1`
    padding-bottom: 1rem;
    height: fit-content;
`;

const SidebarItem = styled.button`
    background: ${({active}) =>
            active ? convertHexToRGBA(defaultThemeColors.blue, 0.05) :  "none"};

    color: ${({active}) =>
            active ? defaultThemeColors.blue : defaultThemeColors.txt};
    border: none;
    padding: 1rem;
    text-align: left;
    cursor: pointer;
    font-family: Montserrat;
    font-size: ${fonts.sizes.regular};
    font-weight: 600;

    border-bottom: ${({active}) =>
            active ? null : `2px solid ${defaultThemeColors.lightBlue};`};

    &:hover {
        background-color: ${convertHexToRGBA(defaultThemeColors.blue, 0.05)};
        color: ${defaultThemeColors.blue};
    }

    justify-content: space-between;
    display: flex;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const SidebarContent = styled.div`
    display: ${({active}) => (active ? "flex" : "none")};
    flex-grow: 1;
    flex: 1;
    height: 100%;
    flex-direction: column;
    width: 100%;
`;

const MobileWrapper = styled.div`
    display: block;

    @media (min-width: 1024px) {
        ${(props) =>
                props.hide &&
                `
      display: none;
    `}
    }
`;

const MobileOverlay = styled.div`
    background-color: ${defaultThemeColors.white};
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: ${(props) => (props.isVisible ? "93%" : "0")};
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    transition: height 0.3s ease-in-out;

    overflow: hidden;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;

    @media (min-width: 1024px) {
        display: none;
    }
`;

const OverlayContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const OverlayHeader = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 2rem;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #eee;

    h2 {
        margin: 0;
        font-size: 1.2rem;
        color: #333;
    }
`;

const CloseButton = styled.button`
    background: none;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    align-content: center;

    padding: 0.5rem;
    border: 1px solid ${themeColor("mobileInputBorder")};
    border-radius: 6px;
`;

const OverlayBody = styled.div`
    flex: 1;
    padding: 16px;
    overflow-y: auto;
`;

